import { useMemo } from "react";
import { CalendarDays, IndianRupee } from "lucide-react";
import SafeHtml from "components/shared/helpers/SafeHtml";
import Table from "components/shared/Table/Table";
import Breadcrumb from "components/shared/Breadcrumb/Breadcrumb";
import { Loader } from "../Common/Loader";
import getHexColor from "utils/getHexColor";
import { strings } from "utils/constants/strings";
import { GRN_DISPUTE_ISSUE_TYPE } from "utils/constants/others";
import { handleActionType } from "helpers/app_router";
import { ACTION_TYPES } from "utils/constants/app_router";
import {
  Actions,
  ActionButton,
  DesktopPage,
  SummaryCard,
  SummaryIcon,
  SummaryItem,
  SummaryValue,
  Title,
  TitleBlock,
  TitleRow,
} from "components/core/Common/DesktopHelpers/DesktopHelpers";
import {
  CellText,
  ProductImage,
  ProductsCard,
  ProductsTitle,
  SummaryLabel,
  SummaryText,
} from "../PO/PoDetails/styles.poDetailsDesktop";
import {
  CellStack,
  ClaimText,
  GrnSummaryCardDesktop,
  GrnSummaryField,
  GrnSummaryGrid,
  GrnSummaryTitle,
  HeaderTagBadge,
  LinkButton,
} from "./styles.grnDesktop";
import SharedInfoBanner from "components/shared/Label/InfoLabel";
import GrnDetailsModals from "./GrnDetailsModals";
import GrnDisputeDrawer from "./GrnDisputeDrawer";

const formatAmount = (amount) => `₹${Number(amount || 0).toLocaleString("en-IN")}`;

const GrnDetailsDesktop = (props) => {
  const {
    source,
    grn,
    grnItems,
    showLoader,
    navigate,
    isDisputeAllowed,
    itemDisputeMap,
    isInDispute,
    grnSummary,
    grnDetailsHeaderTag,
    toggleDiscrepancyModal,
    handleConfirmGrn,
    disputeDrawerOpen,
    setDisputeDrawerOpen,
  } = props;

  const unit = grnItems[0]?.buy_measurement_unit || "unit";
  const summaryRows = useMemo(
    () => [...(grnSummary?.rows || [])].sort((a, b) => a.index - b.index),
    [grnSummary]
  );

  const columns = useMemo(
    () => [
      {
        id: "image",
        header: strings.grnDesktopProductImage,
        meta: { width: 160 },
        cell: (info) => <ProductImage src={info.row.original.product_image} alt="" />,
      },
      {
        id: "name",
        header: strings.grnDesktopProductName,
        meta: { width: 320 },
        cell: (info) => <CellText>{info.row.original.product_name}</CellText>,
      },
      {
        id: "price",
        header: `Price/${unit}`,
        meta: { width: 200 },
        cell: (info) => {
          const item = info.row.original;
          const claims = itemDisputeMap[item.id] ?? {};
          const priceClaim = claims[GRN_DISPUTE_ISSUE_TYPE.PRICE_PER_UNIT];
          const gstClaim = claims[GRN_DISPUTE_ISSUE_TYPE.GST_PERCENTAGE];
          return (
            <CellStack>
              <CellText>
                {formatAmount(item.grn_price_per_unit)}/{item.buy_measurement_unit}
              </CellText>
              {isInDispute && priceClaim ? (
                <ClaimText>
                  Your claim: ₹{priceClaim}/{item.buy_measurement_unit}
                </ClaimText>
              ) : null}
              {isInDispute && gstClaim ? <ClaimText>Your GST claim: {gstClaim}%</ClaimText> : null}
            </CellStack>
          );
        },
      },
      {
        id: "invoice_qty",
        header: strings.invoicedQty || "Invoice qty",
        meta: { width: 140 },
        cell: (info) => (
          <CellText>
            {info.row.original.invoiced_quantity} {info.row.original.buy_measurement_unit}
          </CellText>
        ),
      },
      {
        id: "grn_qty",
        header: strings.grnDesktopGrnQtyHeader,
        meta: { width: 200 },
        cell: (info) => {
          const item = info.row.original;
          const qtyClaim = (itemDisputeMap[item.id] ?? {})[GRN_DISPUTE_ISSUE_TYPE.GRN_QUANTITY];
          return (
            <CellStack>
              <CellText>
                {item.grn_quantity} {item.buy_measurement_unit}
              </CellText>
              {isInDispute && qtyClaim ? (
                <ClaimText>
                  Your claim: {qtyClaim} {item.buy_measurement_unit}
                </ClaimText>
              ) : null}
              {item.discrepancy_qty_reasons?.length ? (
                <LinkButton type="button" onClick={() => toggleDiscrepancyModal(item)}>
                  {strings.discrepancyReasons}
                </LinkButton>
              ) : null}
            </CellStack>
          );
        },
      },
      {
        id: "grn_amount",
        header: strings.grnDesktopGrnAmountHeader,
        cell: (info) => (
          <CellText>
            {formatAmount(Number(info.row.original.grn_quantity || 0) * Number(info.row.original.grn_price_per_unit || 0))}
          </CellText>
        ),
      },
    ],
    [unit, itemDisputeMap, isInDispute, toggleDiscrepancyModal]
  );

  if (showLoader || !grn) {
    return (
      <DesktopPage>
        <Loader />
        <GrnDetailsModals {...props} />
      </DesktopPage>
    );
  }

  const infoLabel = grnSummary?.info_label;
  const canConfirm = !!grn?.grn_page_data?.can_confirm_grn;

  return (
    <DesktopPage>
      <TitleRow>
        <TitleBlock>
          <Breadcrumb
            items={[
              { label: strings.grnDesktopBreadcrumbHome },
              { label: strings.grnDesktopBreadcrumbGrns, onClick: () => handleActionType(ACTION_TYPES.SELLER_GRN_PENDING, {}, navigate) },
              { label: grn?.grn_number },
            ]}
          />
          <TitleRow>
            <Title>{grn.grn_number}</Title>
            {grnDetailsHeaderTag?.text ? (
              <HeaderTagBadge
                $bg={getHexColor(grnDetailsHeaderTag.bg_color)}
                $color={getHexColor(grnDetailsHeaderTag.color)}
              >
                {grnDetailsHeaderTag.text}
              </HeaderTagBadge>
            ) : null}
          </TitleRow>
        </TitleBlock>
        {canConfirm ? (
          <Actions>
            {isDisputeAllowed ? (
              <ActionButton type="button" onClick={() => setDisputeDrawerOpen(true)}>
                  {strings.grnDesktopRaiseDispute}
                </ActionButton>
            ) : null}
            <ActionButton type="button" $primary onClick={handleConfirmGrn}>
              {strings.grnDesktopConfirmGrn}
            </ActionButton>
          </Actions>
        ) : null}
      </TitleRow>

      {infoLabel?.text ? (
        <SharedInfoBanner
          icon={infoLabel.text.includes("resolved") ? "/logos/order-history.svg" : "/logos/exclamation-line.svg"}
          $bg={getHexColor(infoLabel.bg_color)}
          $color={getHexColor(infoLabel.color)}
          $fontSize="14px"
        >
          <SafeHtml
            tag="span"
            style={{ color: getHexColor(infoLabel.color), lineHeight: "1.5", fontSize: "14px", fontWeight: "400" }}
            html={infoLabel.text}
          />
        </SharedInfoBanner>
      ) : null}

      {grnSummary ? (
        <SummaryCard>
          <SummaryItem>
            <SummaryIcon>
              <IndianRupee size={20} />
            </SummaryIcon>
            <SummaryText>
              <SummaryLabel>{grnSummary.invoice_amount?.label}</SummaryLabel>
              <SummaryValue>{grnSummary.invoice_amount?.value}</SummaryValue>
            </SummaryText>
          </SummaryItem>
          <SummaryItem>
            <SummaryIcon>
              <IndianRupee size={20} />
            </SummaryIcon>
            <SummaryText>
              <SummaryLabel>{grnSummary.grn_amount?.label}</SummaryLabel>
              <SummaryValue>{grnSummary.grn_amount?.value}</SummaryValue>
            </SummaryText>
          </SummaryItem>
          <SummaryItem>
            <SummaryIcon>
              <CalendarDays size={20} />
            </SummaryIcon>
            <SummaryText>
              <SummaryLabel>{strings.grnDesktopGrnDoneOn}</SummaryLabel>
              <SummaryValue>{grn.grn_page_data?.grn_completed_on_date_str || "—"}</SummaryValue>
            </SummaryText>
          </SummaryItem>
        </SummaryCard>
      ) : null}

      {summaryRows.length > 0 ? (
        <GrnSummaryCardDesktop>
          <GrnSummaryTitle>{strings.grnDesktopGrnSummaryTitle}</GrnSummaryTitle>
          <GrnSummaryGrid>
            {summaryRows.map((row) => (
              <GrnSummaryField key={row.index}>
                <span>{row.label}</span>
                <span>{row.value}</span>
              </GrnSummaryField>
            ))}
          </GrnSummaryGrid>
        </GrnSummaryCardDesktop>
      ) : null}

      <ProductsCard>
        <ProductsTitle>{strings.grnDesktopProductDetails}</ProductsTitle>
        <Table
          columns={columns}
          data={grnItems}
          getRowId={(row, index) => String(row.id ?? row.purchase_order_item_id ?? index)}
          emptyMessage={strings.grnDesktopNoProductsFound}
        />
      </ProductsCard>

      {disputeDrawerOpen ? (
        <GrnDisputeDrawer
          grn={grn}
          grnItems={grnItems}
          source={source}
          purchaseOrderId={props.purchaseOrderId}
          grnId={props.grnId}
          onClose={() => setDisputeDrawerOpen(false)}
        />
      ) : null}

      <GrnDetailsModals {...props} />
    </DesktopPage>
  );
};

export default GrnDetailsDesktop;
